import { useState, useEffect, useRef, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, TrendingUp, FolderOpen, FileText, Compass, BookOpen, Map, Briefcase, Filter } from "lucide-react";
import { useArticles, useCategories, useArticleTypes } from "@/hooks/useArticles";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface SearchContentProps {
  onClose: () => void;
}

const articleIcons = [FileText, BookOpen, Compass, Map];
const categoryIcons = [FolderOpen, Briefcase, Compass, Map];

function getArticleIcon(index: number) {
  const IconComponent = articleIcons[index % articleIcons.length];
  return <IconComponent className="w-4 h-4 text-primary" />;
}

function getCategoryIcon(index: number) {
  const IconComponent = categoryIcons[index % categoryIcons.length];
  return <IconComponent className="w-4 h-4 text-accent-foreground" />;
}

export const SearchContent = forwardRef<HTMLDivElement, SearchContentProps>(({ onClose }, ref) => {
  const [query, setQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const { data: articles } = useArticles();
  const { data: categories } = useCategories();
  const { data: articleTypes } = useArticleTypes();

  // Focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  // Filter results based on query, category, and type
  const filteredArticles = articles?.filter(article => {
    // Category filter
    if (selectedCategoryId && article.category_id !== selectedCategoryId) {
      return false;
    }
    // Type filter
    if (selectedTypeId && article.article_type_id !== selectedTypeId) {
      return false;
    }
    // Text search (or show all if filters are active without query)
    if (!query.trim()) {
      return selectedCategoryId || selectedTypeId;
    }
    const searchLower = query.toLowerCase();
    return (
      article.title.toLowerCase().includes(searchLower) ||
      article.excerpt.toLowerCase().includes(searchLower) ||
      article.content.toLowerCase().includes(searchLower)
    );
  }).slice(0, 8) || [];

  const filteredCategories = categories?.filter(category => {
    if (!query.trim()) return false;
    if (selectedCategoryId || selectedTypeId) return false; // Hide categories when filters active
    const searchLower = query.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      (category.description?.toLowerCase().includes(searchLower))
    );
  }).slice(0, 3) || [];

  // Has active filters
  const hasActiveFilters = selectedCategoryId || selectedTypeId;

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategoryId(null);
    setSelectedTypeId(null);
  };

  // Popular searches when no query
  const popularArticles = articles?.slice(0, 4) || [];

  const handleSelect = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <div ref={ref} className="flex flex-col">
      {/* Search input */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Søg efter artikler..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground text-sm outline-none"
        />
        {query && (
          <button 
            onClick={() => setQuery("")}
            className="p-1 hover:bg-muted rounded transition-colors"
          >
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        )}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={cn(
            "p-1.5 rounded transition-colors",
            showFilters || hasActiveFilters ? "bg-primary/10 text-primary" : "hover:bg-muted text-muted-foreground"
          )}
          title="Vis filtre"
        >
          <Filter className="w-4 h-4" />
        </button>
      </div>

      {/* Filter section */}
      {showFilters && (
        <div className="px-4 py-3 border-b border-border bg-muted/30 space-y-3">
          {/* Category filters */}
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Kategori
            </p>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setSelectedCategoryId(null)}
                className={cn(
                  "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                  !selectedCategoryId 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                Alle
              </button>
              {categories?.slice(0, 5).map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategoryId(selectedCategoryId === category.id ? null : category.id)}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                    selectedCategoryId === category.id 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  )}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Type filters */}
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Artikel-type
            </p>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setSelectedTypeId(null)}
                className={cn(
                  "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                  !selectedTypeId 
                    ? "bg-accent text-accent-foreground" 
                    : "bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                Alle
              </button>
              {articleTypes?.slice(0, 6).map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedTypeId(selectedTypeId === type.id ? null : type.id)}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                    selectedTypeId === type.id 
                      ? "bg-accent text-accent-foreground" 
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  )}
                >
                  {type.name}
                </button>
              ))}
            </div>
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-primary hover:underline"
            >
              Ryd filtre
            </button>
          )}
        </div>
      )}

      {/* Results */}
      <div className="max-h-[50vh] overflow-y-auto">
        {query.trim() || hasActiveFilters ? (
          <>
            {/* Category results */}
            {filteredCategories.length > 0 && (
              <div className="p-3 border-b border-border">
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
                  Kategorier
                </h3>
                <div className="space-y-0.5">
                  {filteredCategories.map((category, index) => (
                    <button
                      key={category.id}
                      onClick={() => handleSelect(`/kategori/${category.slug}`)}
                      className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted transition-colors text-left group"
                    >
                      <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center shrink-0">
                        {getCategoryIcon(index)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {category.name}
                        </p>
                      </div>
                      <ArrowRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Article results */}
            {filteredArticles.length > 0 && (
              <div className="p-3">
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
                  Artikler
                </h3>
                <div className="space-y-0.5">
                  {filteredArticles.map((article, index) => (
                    <button
                      key={article.id}
                      onClick={() => handleSelect(`/artikel/${article.slug}`)}
                      className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted transition-colors text-left group"
                    >
                      <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                        {getArticleIcon(index)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {article.title}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {article.excerpt}
                        </p>
                      </div>
                      <ArrowRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No results */}
            {filteredArticles.length === 0 && filteredCategories.length === 0 && (
              <div className="p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {hasActiveFilters 
                    ? "Ingen artikler matcher de valgte filtre"
                    : `Ingen resultater for "${query}"`
                  }
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-primary hover:underline mt-2"
                  >
                    Ryd filtre
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          /* Popular searches */
          <div className="p-3">
            <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
              Populære artikler
            </h3>
            <div className="space-y-0.5">
              {popularArticles.map((article) => (
                <button
                  key={article.id}
                  onClick={() => handleSelect(`/artikel/${article.slug}`)}
                  className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted transition-colors text-left group"
                >
                  <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center shrink-0">
                    <TrendingUp className="w-4 h-4 text-accent-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {article.title}
                    </p>
                  </div>
                  <ArrowRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer hint - only on desktop */}
      {!isMobile && (
        <div className="px-4 py-2 border-t border-border bg-muted/30">
          <p className="text-[10px] text-muted-foreground text-center">
            Tryk <kbd className="px-1.5 py-0.5 rounded bg-muted border border-border font-mono text-[10px]">⌘K</kbd> eller <kbd className="px-1.5 py-0.5 rounded bg-muted border border-border font-mono text-[10px]">Ctrl+K</kbd> for at søge • <kbd className="px-1 py-0.5 rounded bg-muted border border-border font-mono text-[10px]">ESC</kbd> for at lukke
          </p>
        </div>
      )}
    </div>
  );
});

SearchContent.displayName = "SearchContent";
